// importaciones de vue
import { onUnmounted, ref, watch, computed } from 'vue';

// importaciones de tipos
import type { ApiResult, ApiFailure, ApiSuccess } from '@/core/either';
import {
  type PaginatedResponse,
  type PaginationParams,
  type PaginationStatus,
  type DataStatus,
  type SearchStatus,
} from './use-pagination.types';

interface UsePaginationOptions<T, R> {
  pageSize?: number;
  debounceTime?: number;
  fetchService: (
    params: PaginationParams
  ) => Promise<ApiResult<PaginatedResponse<R> | R>>;
  transformData?: (data: R[] | any) => T[];
  onError?: (error: ApiFailure) => void;
  onSuccess?: (data: T[]) => void;
  autoLoad?: boolean;
  dataKey?: string;
}

export const usePagination = <T = any, R = T>(
  options: UsePaginationOptions<T, R>
) => {
  const {
    pageSize = 10,
    debounceTime = 600,
    fetchService,
    transformData,
    onError,
    onSuccess,
    autoLoad = true,
    dataKey,
  } = options;

  // reactive state
  const items = ref<T[]>([]);
  const internalPage = ref<number>(0);

  const pagination = ref<PaginationStatus>({
    pageSize,
    totalItems: 0,
    totalPages: 0,
    isFirstPage: true,
    isLastPage: false,
  });

  // Computed bidireccional para currentPage (base 1 frontend <-> base 0 backend)
  const currentPage = computed({
    get: () => internalPage.value + 1,
    set: (value: number) => {
      internalPage.value = value - 1;
    },
  });

  const dataStatus = ref<DataStatus>({
    loading: false,
    error: false,
    isEmpty: false,
    errorDetails: undefined,
  });

  const search = ref<SearchStatus>({
    loading: false,
    value: '',
  });

  // internal variables
  let searchTimeout: NodeJS.Timeout | null = null;

  /**
   * Resetea el estado de error
   */
  const resetError = () => {
    dataStatus.value.error = false;
    dataStatus.value.errorDetails = undefined;
  };

  /**
   * Construye los parámetros de la petición
   */
  const buildParams = (): PaginationParams => {
    const params: PaginationParams = {
      page: internalPage.value,
      size: pagination.value.pageSize,
    };

    if (search.value.value.trim()) {
      params.searchParameter = search.value.value.trim();
      params.search = search.value.value.trim();
    }

    return params;
  };

  /**
   * Extrae los datos de paginación según el dataKey
   */
  const extractPaginationData = (response: any): PaginatedResponse<R> => {
    if (dataKey) {
      return response[dataKey];
    }
    return response;
  };

  /**
   * Actualiza el estado de paginación basado en la respuesta
   */
  const updatePaginationStatus = (response: PaginatedResponse<R>) => {
    pagination.value.totalItems = response.totalElements;
    pagination.value.totalPages = response.totalPages;
    pagination.value.isFirstPage = response.first;
    pagination.value.isLastPage = response.last;
  };

  /**
   * Maneja el éxito de la petición
   */
  const handleSuccess = (response: ApiSuccess<any>) => {
    const paginationData = extractPaginationData(response.data);

    const data = transformData
      ? transformData(paginationData.content)
      : (paginationData.content as unknown as T[]);

    items.value = data;
    dataStatus.value.isEmpty = data.length === 0;
    updatePaginationStatus(paginationData);

    onSuccess?.(data);
  };

  /**
   * Maneja el error de la petición
   */
  const handleError = (error: ApiFailure) => {
    dataStatus.value.error = true;
    dataStatus.value.isEmpty = true;
    dataStatus.value.errorDetails = error;
    items.value = [];

    onError?.(error);
  };

  /**
   * Carga los datos con los parámetros actuales
   */
  const loadData = async () => {
    dataStatus.value.loading = true;
    items.value = [];
    resetError();

    const params = buildParams();
    const result = await fetchService(params);

    result.fold(
      (error) => handleError(error),
      (success) => handleSuccess(success)
    );

    dataStatus.value.loading = false;
  };

  /**
   * Realiza una búsqueda sin debounce
   */
  const searchData = async (value: string) => {
    search.value.loading = true;
    search.value.value = value;

    internalPage.value = 0;

    await loadData();

    search.value.loading = false;
  };

  /**
   * Realiza una búsqueda con debounce
   */
  const searchDataDebounced = (value: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    search.value.value = value;

    if (value.trim() === '') {
      internalPage.value = 0;
      loadData();
      return;
    }

    searchTimeout = setTimeout(() => searchData(value), debounceTime);
  };

  /**
   * Cambia a una página específica (recibe página base 1 del frontend)
   */
  const goToPage = (page: number) => {
    if (page < 1 || page > pagination.value.totalPages) {
      return;
    }
    currentPage.value = page; // Se convierte automáticamente a base 0
  };

  /**
   * Va a la página siguiente
   */
  const nextPage = () => {
    if (!pagination.value.isLastPage) {
      internalPage.value += 1;
    }
  };

  /**
   * Va a la página anterior
   */
  const prevPage = () => {
    if (!pagination.value.isFirstPage) {
      internalPage.value -= 1;
    }
  };

  /**
   * Cambia el tamaño de página
   */
  const changePageSize = (size: number) => {
    pagination.value.pageSize = size;
    internalPage.value = 0;
  };

  /**
   * Resetea la búsqueda
   */
  const clearSearch = () => {
    search.value.value = '';
    internalPage.value = 0;
    loadData();
  };

  /**
   * Refresca los datos actuales
   */
  const refresh = () => {
    return loadData();
  };

  /**
   * Resetea todo el estado
   */
  const reset = () => {
    items.value = [];
    search.value.value = '';
    internalPage.value = 0;
    dataStatus.value = {
      loading: false,
      error: false,
      isEmpty: false,
      errorDetails: undefined,
    };
  };

  // watchers
  watch(internalPage, () => {
    loadData();
  });

  watch(
    () => pagination.value.pageSize,
    () => {
      loadData();
    },
    { immediate: false }
  );

  // lifecycle
  onUnmounted(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  // initial load
  if (autoLoad) {
    loadData();
  }

  return {
    // state
    items,
    pagination,
    dataStatus,
    search,
    currentPage,

    // methods
    loadData,
    searchData,
    searchDataDebounced,
    goToPage,
    nextPage,
    prevPage,
    changePageSize,
    clearSearch,
    refresh,
    reset,
  };
};
